// Hook that runs after returning from Stripe Checkout.
//
// Hardened return flow:
//  1. Verify session_id with Stripe (customer + active/trialing) before any
//     "success" UI — prevents spoofed ?upgrade=success URLs from lying.
//  2. Retry sync with exponential backoff (500ms, 2s, 5s) on transient errors.
//  3. Surface persistent red error UI on failure (caller renders banner from
//     status + errorKind). Never silently reset, never reload on failure.
//  4. Detect auth-gone-mid-flow: if accessToken missing, route to "auth_lost"
//     and preserve URL params across re-auth.
import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  syncSubscriptionFromStripe,
  verifyCheckoutSession,
} from "@/lib/billing.functions";
import { supabase } from "@/integrations/supabase/client";

const USER_KEY = "menotrack_user";
const PENDING_RETURN_KEY = "menoiq_pending_stripe_return";

export type StripeReturnStatus =
  | "idle"
  | "syncing"
  | "success"
  | "cancelled"
  | "auth_lost"
  | "error";

export type StripeReturnState = {
  status: StripeReturnStatus;
  errorKind?: "verify" | "transient" | "auth" | "unknown";
  retry: () => void;
  dismiss: () => void;
};

// Classify whether a thrown error is worth retrying.
function isTransientError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  if (!msg) return true;
  if (msg.startsWith("VERIFY_FAILED:")) return false;
  if (/\b(401|403|404|422)\b/.test(msg)) return false;
  if (/Not signed in/i.test(msg)) return false;
  // Transient-ish: network blips, fetch failures, 5xx, timeouts.
  if (/network|fetch|timeout|ECONN|ENET|5\d\d/i.test(msg)) return true;
  // Default to transient — better to retry than to dead-end.
  return true;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useStripeReturnHandler(userId: string): StripeReturnState {
  const sync = useServerFn(syncSubscriptionFromStripe);
  const verify = useServerFn(verifyCheckoutSession);
  const [status, setStatus] = useState<StripeReturnStatus>("idle");
  const [errorKind, setErrorKind] = useState<StripeReturnState["errorKind"]>();
  const handled = useRef(false);
  const lastSessionId = useRef<string | null>(null);

  const stripParamsAndPersist = useCallback((persist: boolean) => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (persist && lastSessionId.current) {
      try {
        window.sessionStorage.setItem(
          PENDING_RETURN_KEY,
          JSON.stringify({ sessionId: lastSessionId.current }),
        );
      } catch {}
    }
    params.delete("upgrade");
    params.delete("session_id");
    const next = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
    window.history.replaceState({}, "", next);
  }, []);

  const runFlow = useCallback(
    async (sessionId: string | null) => {
      setStatus("syncing");
      setErrorKind(undefined);

      // Auth check first — bail to auth_lost if the session is gone.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        // Persist the session_id so we can resume after re-auth.
        if (sessionId) {
          try {
            window.sessionStorage.setItem(
              PENDING_RETURN_KEY,
              JSON.stringify({ sessionId }),
            );
          } catch {}
        }
        setErrorKind("auth");
        setStatus("auth_lost");
        return;
      }

      // 1. Verify session_id before showing success. Skip only if missing
      //    entirely (legacy flow) — in that case fall through to sync.
      if (sessionId) {
        try {
          await verify({ data: { accessToken, sessionId } });
        } catch (e) {
          console.error("[stripe-return] verify failed", e);
          setErrorKind("verify");
          setStatus("error");
          return;
        }
      }

      // 2. Sync with retry/backoff on transient errors only.
      const delays = [500, 2000, 5000];
      let lastErr: unknown = null;
      for (let attempt = 0; attempt <= delays.length; attempt++) {
        try {
          await sync({ data: { accessToken } });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          if (!isTransientError(e) || attempt === delays.length) break;
          await sleep(delays[attempt]);
        }
      }
      if (lastErr) {
        console.error("[stripe-return] sync failed after retries", lastErr);
        setErrorKind("transient");
        setStatus("error");
        return;
      }

      // 3. Re-pull non-entitlement profile fields and refresh localStorage so
      //    KnowMenoApp picks up name/stage edits immediately. Entitlement
      //    (is_premium / isPlus) is intentionally NOT written here — plan
      //    state lives in ProfileSync (usePlan), sourced server-side. Writing
      //    isPremium to localStorage would let a DevTools edit spoof premium.
      try {
        const { data } = await supabase
          .from("profiles")
          .select(
            "name,email,life_stage,focus_area,onboarding_completed,trial_started_at",
          )
          .eq("id", userId)
          .maybeSingle();
        if (data) {
          const existingRaw = window.localStorage.getItem(USER_KEY);
          const existing = existingRaw ? JSON.parse(existingRaw) : {};
          const updated = {
            ...existing,
            name: data.name ?? existing.name ?? "",
            email: data.email ?? existing.email ?? "",
            lifeStage: data.life_stage ?? existing.lifeStage ?? "",
            focusArea: data.focus_area ?? existing.focusArea ?? "",
            healthProfileComplete:
              data.onboarding_completed || !!existing.healthProfileComplete,
            signupDate:
              data.trial_started_at ||
              existing.signupDate ||
              new Date().toISOString(),
          };
          // Strip any legacy isPremium key from prior versions.
          if ("isPremium" in updated) delete (updated as Record<string, unknown>).isPremium;
          window.localStorage.setItem(USER_KEY, JSON.stringify(updated));
        }
      } catch (e) {
        console.error("[stripe-return] profile refresh failed", e);
        // Non-fatal — sync succeeded, user state will catch up on next load.
      }

      try {
        window.sessionStorage.removeItem(PENDING_RETURN_KEY);
      } catch {}

      setStatus("success");
      // Conversion signal — no pre-existing PostHog event for subscription
      // success existed, so emit one here. PostHog person properties
      // (launch_comp / launch_comp_granted_at) are set on identify in
      // ProfileSync, so this event is filterable by comp cohort.
      try {
        const ph = (window as any).posthog;
        if (ph?.capture) {
          ph.capture("subscription_started", {
            session_id: lastSessionId.current ?? null,
          });
        }
      } catch {}
      stripParamsAndPersist(false);
      // Reload only on success so KnowMenoApp re-bootstraps with new user object.
      window.setTimeout(() => window.location.reload(), 600);
    },
    [sync, verify, userId, stripParamsAndPersist],
  );

  useEffect(() => {
    if (handled.current) return;
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const flag = params.get("upgrade");
    const sessionId = params.get("session_id");

    // Check for pending re-auth resume even without URL flag.
    let pendingSessionId: string | null = null;
    try {
      const raw = window.sessionStorage.getItem(PENDING_RETURN_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { sessionId?: string };
        pendingSessionId = parsed.sessionId ?? null;
      }
    } catch {}

    if (!flag && !pendingSessionId) return;
    handled.current = true;

    if (flag === "cancelled") {
      setStatus("cancelled");
      params.delete("upgrade");
      const next = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
      window.history.replaceState({}, "", next);
      window.setTimeout(() => setStatus("idle"), 4000);
      return;
    }

    if (flag === "success" || pendingSessionId) {
      const sid = sessionId ?? pendingSessionId;
      lastSessionId.current = sid;
      void runFlow(sid);
    }
  }, [runFlow]);

  const retry = useCallback(() => {
    if (status !== "error") return;
    handled.current = true;
    void runFlow(lastSessionId.current);
  }, [runFlow, status]);

  const dismiss = useCallback(() => {
    setStatus("idle");
    setErrorKind(undefined);
    try {
      window.sessionStorage.removeItem(PENDING_RETURN_KEY);
    } catch {}
  }, []);

  return { status, errorKind, retry, dismiss };
}
